import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { appToast } from '../../../../components/AppToast'
import { appConfirm } from '../../../../components/AppConfirmModal'

export default function SyllabusNotesModal({
  isOpen = false,
  item = null,
  onClose,
  onSaveNotes,
}) {
  const [selectedFile, setSelectedFile] = useState(null)
  const [isUploading, setIsUploading] = useState(false)

  if (!isOpen || !item) return null

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      appToast.error('Only PDF documents are allowed for chapter notes.')
      return
    }
    setSelectedFile(file)
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      appToast.error('Please select a PDF file to upload.')
      return
    }
    setIsUploading(true)
    try {
      await onSaveNotes(item.id, true, selectedFile.name)
      appToast.success(`Notes uploaded successfully for ${item.chapterName}`)
      setSelectedFile(null)
      onClose()
    } catch (e) {
      appToast.error('Failed to upload notes.')
    } finally {
      setIsUploading(false)
    }
  }

  const handleDelete = async () => {
    const ok = await appConfirm({
      title: 'Delete Notes',
      message: 'Are you sure you want to delete the attached notes?',
      confirmText: 'Delete',
      isDestructive: true,
    })
    if (!ok) return
    setIsUploading(true)
    try {
      await onSaveNotes(item.id, false, '')
      appToast.success(`Notes deleted for ${item.chapterName}`)
      onClose()
    } catch (e) {
      appToast.error('Failed to delete notes.')
    } finally {
      setIsUploading(false)
    }
  }

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4 overflow-hidden">
      <div className="w-full max-w-xl rounded-3xl border border-outline-variant/25 bg-surface-container-lowest shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="border-b border-outline-variant/20 bg-surface-container-low/60 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <span className="material-symbols-outlined text-lg">description</span>
            </div>
            <div>
              <h3 className="font-display text-base font-bold text-on-surface">Upload Notes</h3>
              <p className="text-xs text-on-surface-variant line-clamp-1">
                Chapter {item.chapterNo}: {item.chapterName}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-on-surface-variant hover:bg-surface-container-high transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 flex flex-col gap-5">
          {/* Current Status */}
          {item.hasNotes ? (
            <div className="flex items-center justify-between rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-xs">
              <div className="flex items-center gap-3">
                <span className="material-symbols-outlined text-2xl text-emerald-600">picture_as_pdf</span>
                <div>
                  <p className="font-bold text-on-surface">{item.notesFilename || 'Chapter_Notes.pdf'}</p>
                  <p className="text-[11px] text-emerald-700 font-semibold">Active PDF Attachment</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleDelete}
                className="rounded-xl border border-error/30 bg-surface-container-lowest px-3 py-1.5 text-xs font-bold text-error hover:bg-error/10 transition cursor-pointer"
              >
                Delete File
              </button>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-outline-variant/40 bg-surface-container-low/50 p-6 text-center text-xs">
              <span className="material-symbols-outlined text-3xl text-outline mb-1">cloud_upload</span>
              <p className="font-bold text-on-surface">No notes attached yet</p>
              <p className="text-[11px] text-on-surface-variant mt-0.5">Upload a PDF note file for students and parents</p>
            </div>
          )}

          {/* Upload New File */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-bold text-on-surface uppercase tracking-wider">
              Select PDF Document
            </label>
            <input
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleFileChange}
              className="block w-full text-xs text-on-surface file:mr-4 file:rounded-xl file:border-0 file:bg-primary file:px-4 file:py-2 file:text-xs file:font-bold file:text-on-primary hover:file:bg-primary/90 cursor-pointer"
            />
          </div>

          {/* Tips / Policy Box */}
          <div className="rounded-2xl bg-surface-container-low p-4 text-[11px] text-on-surface-variant flex flex-col gap-1.5 border border-outline-variant/15">
            <div className="flex items-center gap-1.5 font-bold text-on-surface">
              <span className="material-symbols-outlined text-sm text-primary">info</span>
              <span>Notes Guidelines:</span>
            </div>
            <ul className="list-disc list-inside space-y-1 pl-1 text-[11px]">
              <li>Only PDF files can be uploaded as curriculum notes.</li>
              <li>Students and parents can view or download notes directly from the portal.</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-outline-variant/20 bg-surface-container-low/60 px-6 py-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-surface-container-high px-5 py-2 text-xs font-bold text-on-surface hover:bg-surface-container-highest transition"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!selectedFile || isUploading}
            onClick={handleUpload}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-2 text-xs font-bold text-on-primary shadow-sm transition hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined text-sm">upload</span>
            <span>{isUploading ? 'Uploading...' : 'Save & Upload'}</span>
          </button>
        </div>

      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}
